import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Menu from "../components/menu";
import ProfileInfo from "../components/profile-info";
import DashboardShell from "../../components/DashboardShell";
import { useMenuState } from "../../Common/MenuStateContext";
import ContentLoader from "../../components/ContentLoader";
import { getProjectUserList } from "../../services/pofile";
import { getAssessSubmitMemberCount } from "../../services/dashboard";
import { fetchAssessmentSubmitMemberCount } from "../../services/assessment";
import { getActiveChatCount } from "../../services/chatApi";
import { useAuth } from "../../auth/AuthContext";
import "../../assets/css/dashboard.css";

type LoadState = "idle" | "loading" | "ready" | "error";

interface ActiveChatSummary {
  userName?: string;
  interactionCount?: number;
  lastInteractionDate?: string;
}

const QUICK_LINKS = [
  { label: "Team Members", path: "/su/team_member" },
  { label: "Capability Assessment", path: "/su/cap_assess" },
  { label: "Reports", path: "/su/reports" },
];

const readStoredProjectId = () => {
  const stored = localStorage.getItem("projectId");
  if (!stored) return null;
  const parsed = Number(stored);
  return Number.isFinite(parsed) && parsed > 0 ? String(parsed) : null;
};

const extractSubmittedCount = (response: unknown): number => {
  if (response == null || typeof response !== "object") return 0;
  const body = response as {
    data?: unknown;
    message?: string;
  };

  if (typeof body.data === "number") {
    return body.data;
  }

  if (body.data && typeof body.data === "object") {
    const count = (body.data as { totalMembersCount?: number; TotalMembersCount?: number })
      .totalMembersCount ??
      (body.data as { TotalMembersCount?: number }).TotalMembersCount;
    if (typeof count === "number" && Number.isFinite(count)) {
      return count;
    }
  }

  // Backend returns null + "no member found" when the count is zero.
  if (
    body.data == null &&
    typeof body.message === "string" &&
    body.message.toLowerCase().includes("no member")
  ) {
    return 0;
  }

  return 0;
};

const extractCompletionPercentage = (response: unknown): number | null => {
  if (response == null || typeof response !== "object") return null;
  const body = response as { data?: { percentage?: number; Percentage?: number } };
  const percentage = body.data?.percentage ?? body.data?.Percentage;
  if (typeof percentage === "number" && Number.isFinite(percentage)) {
    return Math.max(0, Math.min(100, Math.round(percentage)));
  }
  return null;
};

const Dashboard = () => {
  const { isMenuCollapsed, toggleMenuCollapse } = useMenuState();
  const { user } = useAuth();
  const [selectedItem, setSelectedItem] = useState(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [loadState, setLoadState] = useState<LoadState>("idle");
  const [teamCount, setTeamCount] = useState<number | null>(null);
  const [submittedCount, setSubmittedCount] = useState<number | null>(null);
  const [completionRate, setCompletionRate] = useState<number | null>(null);
  const [activeChat, setActiveChat] = useState<ActiveChatSummary | null>(null);

  useEffect(() => {
    const storedId = readStoredProjectId();
    if (storedId) {
      setProjectId(storedId);
      return;
    }

    // Current system: Super Users have a single project — use it automatically.
    const soleProject = user?.projectDetails?.[0];
    if (soleProject?.projectId != null) {
      const soleId = String(soleProject.projectId);
      localStorage.setItem("projectId", soleId);
      setProjectId(soleId);
      return;
    }

    setProjectId(null);
  }, [user?.projectDetails]);

  const selectedProject =
    user?.projectDetails?.find(
      (proj) =>
        projectId != null &&
        String(proj.projectId) === String(projectId)
    ) ??
    (user?.projectDetails?.length === 1 ? user.projectDetails[0] : null);

  const projectName = selectedProject?.projectName || null;
  const organizationName = selectedProject?.companyName || null;

  useEffect(() => {
    if (!projectId || !user?.userId) {
      setLoadState("idle");
      setTeamCount(null);
      setSubmittedCount(null);
      setCompletionRate(null);
      setActiveChat(null);
      return;
    }

    let cancelled = false;

    const loadProjectOverview = async () => {
      setLoadState("loading");
      setTeamCount(null);
      setSubmittedCount(null);
      setCompletionRate(null);
      setActiveChat(null);

      const payload = {
        UserId: user.userId,
        ProjectId: parseInt(projectId, 10),
      };

      const [teamResult, submitResult, completionResult, chatResult] =
        await Promise.allSettled([
          getProjectUserList(payload),
          getAssessSubmitMemberCount(payload),
          fetchAssessmentSubmitMemberCount(payload),
          getActiveChatCount(payload),
        ]);

      if (cancelled) return;

      let hadSuccess = false;

      if (teamResult.status === "fulfilled") {
        const members = teamResult.value?.data;
        setTeamCount(Array.isArray(members) ? members.length : 0);
        hadSuccess = true;
      } else {
        setTeamCount(null);
      }

      if (submitResult.status === "fulfilled") {
        // Backend returns null + "no member found" when count is zero;
        // extractSubmittedCount maps that to 0 instead of perpetual loading.
        setSubmittedCount(extractSubmittedCount(submitResult.value));
        hadSuccess = true;
      } else {
        setSubmittedCount(null);
      }

      if (completionResult.status === "fulfilled") {
        setCompletionRate(extractCompletionPercentage(completionResult.value));
        hadSuccess = true;
      } else {
        setCompletionRate(null);
      }

      if (chatResult.status === "fulfilled") {
        const rows = chatResult.value?.data;
        setActiveChat(Array.isArray(rows) && rows.length > 0 ? rows[0] : null);
        hadSuccess = true;
      } else {
        setActiveChat(null);
      }

      setLoadState(hadSuccess ? "ready" : "error");
    };

    loadProjectOverview();

    return () => {
      cancelled = true;
    };
  }, [projectId, user?.userId]);

  const renderProjectOverview = () => {
    if (loadState === "loading") {
      return <ContentLoader compact label="Loading project overview…" />;
    }

    if (loadState === "error") {
      return (
        <div className="dash-role-empty">
          <p className="dash-role-empty__title">Could not load project stats</p>
          <p className="dash-role-empty__hint">
            Try refreshing the page. If this continues, contact support.
          </p>
        </div>
      );
    }

    return (
      <>
        <div className="dash-role-stats" aria-live="polite">
          <div className="dash-role-stat">
            <span className="dash-role-stat__label">Team members</span>
            <span className="dash-role-stat__value">
              {teamCount === null ? "—" : teamCount.toLocaleString()}
            </span>
          </div>
          <div className="dash-role-stat">
            <span className="dash-role-stat__label">
              Members who completed assessments
            </span>
            <span className="dash-role-stat__value">
              {submittedCount === null ? "—" : submittedCount.toLocaleString()}
            </span>
          </div>
          <div className="dash-role-stat dash-role-stat--accent">
            <span className="dash-role-stat__label">Assessment completion</span>
            <span className="dash-role-stat__value">
              {completionRate === null ? "—" : `${completionRate}%`}
            </span>
            <span className="dash-role-stat__hint">
              Share of team members with assessment activity
            </span>
          </div>
        </div>

        {activeChat?.userName ? (
          <div className="dash-role-highlight">
            <span className="dash-role-highlight__label">Most active in chat</span>
            <span className="dash-role-highlight__value">
              {activeChat.userName}
              {typeof activeChat.interactionCount === "number"
                ? ` · ${activeChat.interactionCount} interactions`
                : ""}
            </span>
            {activeChat.lastInteractionDate ? (
              <span className="dash-role-highlight__meta">
                Last activity{" "}
                {new Date(activeChat.lastInteractionDate).toLocaleString()}
              </span>
            ) : null}
          </div>
        ) : null}
      </>
    );
  };

  return (
    <DashboardShell
      isMenuCollapsed={isMenuCollapsed}
      sidebar={
        <Menu
          onSelect={(item) => {
            setSelectedItem(item);
            return item;
          }}
          selectedItem={selectedItem}
          isCollapsed={isMenuCollapsed}
          onToggleCollapse={toggleMenuCollapse}
        />
      }
    >
      <div className="dash-frame-parent">
        <div className="dash-frame-group">
          <div className="dash-dashboard-wrapper">
            <div className="dash-dashboard app-heading-accent text-[var(--app-heading)]">Super User Dashboard</div>
          </div>
          <ProfileInfo />
        </div>

        <div className="dash-frame-wrapper">
          <div className="dash-frame-div">
            <div className="dash-admin-home">
              <p className="dash-admin-home__welcome">
                Welcome, {user?.userName || "Super User"}!
              </p>
              <p className="dash-admin-home__subtitle">
                Oversee your project&apos;s team progress, assessment
                completion, and collaboration.
              </p>

              {!projectId ? (
                <div className="dash-role-empty">
                  <p className="dash-role-empty__title">No project assigned</p>
                  <p className="dash-role-empty__hint">
                    Your account does not have a project yet. Contact a Super
                    Admin if this looks incorrect.
                  </p>
                </div>
              ) : (
                <>
                  <div className="dash-role-project-banner">
                    <div>
                      <span className="dash-role-project-banner__label">
                        Project
                      </span>
                      <p className="dash-role-project-banner__name">
                        {projectName || "Your project"}
                      </p>
                      <p className="dash-role-project-banner__org">
                        {organizationName || "Organization"}
                      </p>
                    </div>
                  </div>

                  {renderProjectOverview()}
                </>
              )}

              <h2 className="dash-role-section-title">Quick access</h2>
              <div className="dash-admin-home__grid">
                {QUICK_LINKS.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="dash-admin-home__card"
                  >
                    <span className="dash-admin-home__card-label">
                      {link.label}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardShell>
  );
};

export default Dashboard;
